/**
 * ProductModeContext - Manages ScopeOps product mode
 * Supports "full" mode and "scoping-only" (ScopeOps Scoping) mode
 */

import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { api } from '../api/client';

// RBAC FIX: Admin roles that bypass tier restrictions
const ADMIN_BYPASS_ROLES = ['sysadmin', 'admin', 'program_admin'];

// Navigation items for scoping-only mode
const SCOPING_NAV_ITEMS = [
  { key: 'scope', label: 'Scope Workspace', path: '/scope-workspace', icon: 'Target' },
  { key: 'baselines', label: 'Baselines', path: '/scope-baselines', icon: 'GitCompare' },
  { key: 'assistant', label: 'AI Assistant', path: '/ai-scoping-assistant', icon: 'Sparkles' },
];

// Routes hidden in scoping-only mode (redirect to assistant)
const SCOPING_HIDDEN_ROUTES = [
  // Executive Cockpit / Dashboards
  '/dashboard/executive',
  '/dashboard/pmo',
  '/dashboard/project-manager',
  '/dashboard/executive-legacy',
  '/executive-dashboard-v2',
  '/pmo-dashboard-v2',
  '/pm-dashboard-v2',
  '/cockpit',
  
  // Hierarchy Pages
  '/epics',
  '/features',
  '/stories',
  '/tasks',
  
  // Governance / Tracking
  '/issues',
  '/dependencies',
  '/decisions',
  '/releases',
  
  // Portfolio/Program/Project Details (legacy)
  '/portfolio/',
  '/program/',
  '/project/',
  
  // Upload (scoping uses its own upload)
  '/upload-center',
  
  // Notifications (Daily Signal)
  '/notifications',
  
  // Admin Billing (scoping has simpler billing)
  '/admin/billing',
];

// Routes conditionally hidden (depends on feature flags)
const CONDITIONAL_HIDDEN_ROUTES = {
  // Work Items - hidden unless generateWorkItems is true
  workItems: ['/work-items', '/project-work-items'],
};

// Inspector tabs hidden in scoping mode
const SCOPING_HIDDEN_INSPECTOR_TABS = [
  'risks',
  'issues', 
  'dependencies',
  'decisions',
  'milestones',
  'roadmap',
  'children',
  'programs',
];

// Inspector tabs allowed in scoping mode
const SCOPING_ALLOWED_INSPECTOR_TABS = [
  'summary',
  'scope',
  'baselines',
  'coverage',
  'comments',
  'support', // Ask Support AI
];

// Entity types allowed in scoping mode Inspector
const SCOPING_ALLOWED_ENTITY_TYPES = [
  'scopeNode',
  'scope_node',
  'baseline',
  'coverageItem',
  'coverage_item',
  'scope',
  'requirement',
];

const ProductModeContext = createContext(null);

export const ProductModeProvider = ({ children }) => {
  const { user, isAuthenticated, hasRole, isPlatformAdmin } = useAuth();
  const [productMode, setProductMode] = useState('full');
  const [scopingTier, setScopingTier] = useState('enterprise');
  const [features, setFeatures] = useState({});
  const [loading, setLoading] = useState(true);
  
  const tenantId = user?.tenant?.id || user?.tenant_id;
  
  // RBAC FIX: Check if user is an admin who should bypass tier restrictions
  const isAdminBypass = useMemo(() => {
    // Platform admins always bypass
    if (typeof isPlatformAdmin === 'function' && isPlatformAdmin()) return true;
    // Check for admin roles
    if (typeof hasRole === 'function' && hasRole(...ADMIN_BYPASS_ROLES)) return true;
    // Check user object directly as fallback
    if (user?.is_platform_admin) return true;
    if (user?.roles?.some(r => ADMIN_BYPASS_ROLES.includes(r.role_key))) return true;
    return false;
  }, [user, isPlatformAdmin, hasRole]);
  
  // Load product config on mount
  useEffect(() => {
    if (isAuthenticated && tenantId) {
      loadProductConfig();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, tenantId]);
  
  const loadProductConfig = async () => {
    try {
      const res = await api.getProductConfig(tenantId);
      setProductMode(res.data.productMode || 'full');
      setScopingTier(res.data.scopingTier || 'enterprise');
      setFeatures(res.data.features || {});
    } catch (err) {
      console.error('Failed to load product config:', err);
      // Default to full mode on error
      setProductMode('full');
    } finally {
      setLoading(false);
    }
  };
  
  // Derived state
  const isScopingOnly = useMemo(() => productMode === 'scoping-only', [productMode]);
  
  // Get navigation items based on mode
  const getNavItems = useMemo(() => {
    if (isScopingOnly) {
      return SCOPING_NAV_ITEMS;
    }
    return null; // Use default navigation
  }, [isScopingOnly]);
  
  // Get feature flag value
  // RBAC FIX: Admins bypass feature restrictions
  const getFeature = useCallback((path, defaultValue = true) => {
    // RBAC BYPASS: Admins can access all features
    if (isAdminBypass) return true;
    
    const parts = path.split('.');
    let current = features;
    
    for (const part of parts) {
      if (current && typeof current === 'object' && part in current) {
        current = current[part];
      } else {
        return defaultValue;
      }
    }
    
    return current;
  }, [features, isAdminBypass]);
  
  // Check if work items generation is enabled
  // RBAC FIX: Uses getFeature which handles admin bypass
  const canGenerateWorkItems = useMemo(() => {
    return getFeature('scoping.generateWorkItems', true);
  }, [getFeature]);
  
  // Check if a route is allowed
  const isRouteAllowed = (route) => {
    if (!isScopingOnly) return true;
    
    // Check against base hidden routes
    for (const hidden of SCOPING_HIDDEN_ROUTES) {
      if (route.startsWith(hidden)) {
        return false;
      }
    }
    
    // Check conditional routes
    if (!canGenerateWorkItems) {
      for (const conditionalRoute of CONDITIONAL_HIDDEN_ROUTES.workItems) {
        if (route.startsWith(conditionalRoute)) {
          return false;
        }
      }
    }
    
    return true;
  };
  
  // Get redirect path for blocked routes
  const getRedirectPath = (fromRoute = null) => {
    if (isScopingOnly) {
      const base = '/ai-scoping-assistant';
      if (fromRoute) {
        return `${base}?ref=guard&from=${encodeURIComponent(fromRoute)}`;
      }
      return base;
    }
    return '/dashboard/executive';
  };
  
  // Check if an entity type is allowed in Inspector
  const isEntityTypeAllowed = (entityType) => {
    if (!isScopingOnly) return true;
    
    const normalized = entityType?.toLowerCase().replace(/-/g, '_');
    return SCOPING_ALLOWED_ENTITY_TYPES.includes(normalized);
  };
  
  // Check if an Inspector tab is allowed
  const isInspectorTabAllowed = (tabId) => {
    if (!isScopingOnly) return true;
    
    // Work items tab is conditional
    if (tabId === 'workItems' || tabId === 'work_items') {
      return canGenerateWorkItems;
    }
    
    return SCOPING_ALLOWED_INSPECTOR_TABS.includes(tabId);
  };
  
  // Get filtered tabs for Inspector
  const getFilteredInspectorTabs = (tabs) => {
    if (!isScopingOnly) return tabs;
    
    return tabs.filter(tab => {
      const tabId = tab.id || tab.key;
      return isInspectorTabAllowed(tabId);
    });
  };
  
  // Context value
  const value = useMemo(() => ({
    // Mode state
    productMode,
    isScopingOnly,
    scopingTier,
    features,
    loading,
    
    // RBAC FIX: Expose admin bypass state
    isAdminBypass,
    
    // Tier checks - RBAC FIX: Admins bypass tier restrictions
    isBasicTier: isAdminBypass ? false : scopingTier === 'basic',
    isProTier: isAdminBypass ? false : scopingTier === 'pro',
    isEnterpriseTier: isAdminBypass ? true : scopingTier === 'enterprise',
    
    // Tier capabilities (derived) - RBAC FIX: Admins get all capabilities
    canCreateGovernance: isAdminBypass || scopingTier !== 'basic',
    canEditGovernance: isAdminBypass || scopingTier !== 'basic',
    hasUnlimitedBaselines: isAdminBypass || scopingTier !== 'basic',
    hasBenefitsKpis: isAdminBypass || scopingTier !== 'basic',
    hasAdvancedAI: isAdminBypass || scopingTier === 'enterprise',
    
    // Navigation
    getNavItems,
    isRouteAllowed,
    getRedirectPath,
    SCOPING_NAV_ITEMS,
    SCOPING_HIDDEN_ROUTES,
    
    // Feature flags
    getFeature,
    canGenerateWorkItems,
    
    // Inspector controls
    isEntityTypeAllowed,
    isInspectorTabAllowed,
    getFilteredInspectorTabs,
    SCOPING_HIDDEN_INSPECTOR_TABS,
    SCOPING_ALLOWED_INSPECTOR_TABS,
    SCOPING_ALLOWED_ENTITY_TYPES,
    
    // Branding
    productName: isScopingOnly ? 'ScopeOps Scoping' : 'ScopeOps',
    showModeBanner: isScopingOnly,
    modeBannerText: 'ScopeOps Scoping Mode',
    
    // Reload config
    reloadConfig: loadProductConfig,
  }), [productMode, isScopingOnly, scopingTier, features, loading, isAdminBypass, getNavItems, canGenerateWorkItems, getFeature]);
  
  return (
    <ProductModeContext.Provider value={value}>
      {children}
    </ProductModeContext.Provider>
  );
};

export const useProductMode = () => {
  const context = useContext(ProductModeContext);
  if (!context) {
    // Return default values if not in provider
    return {
      productMode: 'full',
      isScopingOnly: false,
      scopingTier: 'enterprise',
      features: {},
      loading: false,
      // Tier checks
      isBasicTier: false,
      isProTier: false,
      isEnterpriseTier: true,
      canCreateGovernance: true,
      canEditGovernance: true,
      hasUnlimitedBaselines: true,
      hasBenefitsKpis: true,
      hasAdvancedAI: true,
      // Navigation
      getNavItems: null,
      isRouteAllowed: () => true,
      getRedirectPath: () => '/dashboard/executive',
      getFeature: () => true,
      canGenerateWorkItems: true,
      isEntityTypeAllowed: () => true,
      isInspectorTabAllowed: () => true,
      getFilteredInspectorTabs: (tabs) => tabs,
      productName: 'ScopeOps',
      showModeBanner: false,
      modeBannerText: '',
    };
  }
  return context;
};

export default ProductModeProvider;
